import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, LineReveal } from "../components/motion";
import BookingCta from "../components/BookingCta";
import { IMAGES } from "../config/site";

const RITUALS = [
  { img: IMAGES.lavender, t: "Wander the lavender", c: "Walk the rows when the lavender is in season, and take the scent of the high country home with you." },
  { img: IMAGES.hottub, t: "Hot tub under the sky", c: "Your dome's private hot tub, best at dusk — warm water, cool air, and the hills going dark around you." },
  { img: IMAGES.sauna, t: "Riverside sauna", c: "Step out of the sauna and into the clean high-country air beside the Maerewhenua River." },
  { img: IMAGES.nightSky, t: "Southern stars", c: "Far from town lights, the valley after dark belongs entirely to the sky. Look up." },
  { img: IMAGES.horses, t: "Farm paddocks", c: "Say hello to the horses, take the slow route by the river, and let the day stay empty on purpose." },
  { img: IMAGES.snow, t: "Winter in the valley", c: "Frost, snow on the hills, and the domes glowing warm — a different kind of quiet." },
];

const TRIPS = [
  ["01", "Naseby", "The village up among the pines — historic gold workings, a pub, and the country's premier curling rink.", null],
  ["02", "Maniototo Adventure Park", "Ice skating, curling and the ice luge under cover in Naseby — a year-round hit whatever the weather.", "https://www.maniototoadventurepark.co.nz/"],
  ["03", "Naseby Forest MTB Trails", "Hand-built mountain bike trails through the Naseby pines — grades 1–5, free to ride.", "https://centralotagonz.com/explore/listing/naseby-forest-mountain-bike-trails"],
  ["04", "Danseys Pass Hotel", "A classic high-country pub serving meals and drinks, over the pass.", "https://www.danseyspass.co.nz/"],
  ["05", "Oteake Conservation Area", "Explore the ranges between Omarama, Saint Bathans and Naseby.", "https://www.doc.govt.nz/parks-and-recreation/places-to-go/otago/places/oteake-conservation-park/"],
  ["06", "Road-trip country", "Queenstown, Wānaka, Aoraki / Mount Cook and Christchurch, all within easy driving distance.", null],
];

export default function Experiences() {
  return (
    <>
      <section className="relative h-[72svh] min-h-[520px] overflow-hidden flex items-end" data-testid="experiences-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.nightSky} alt="Stars over the high country" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/40" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto w-full px-6 md:px-8 pb-16">
          <Eyebrow className="mb-6">Experiences</Eyebrow>
          <LineReveal lines={["Days that", "don't need filling."]} className="display-1 text-cream" />
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-16" data-testid="experiences-grid-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="serif-italic text-2xl md:text-3xl text-sage max-w-2xl leading-snug mb-16">
              “There's no pressure to fill the day. Read a book, explore the lavender fields in
              season, enjoy the sauna, cook something good — or simply watch the light move across the hills.”
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {RITUALS.map((r, i) => (
              <Reveal key={r.t} delay={(i % 3) * 0.1} testid={`experience-card-${i + 1}`}>
                <div className="group h-full">
                  <div className="img-frame aspect-[4/3]">
                    <img src={r.img} alt={r.t} className="h-full w-full object-cover" />
                  </div>
                  <h3 className="font-display text-2xl text-cream mt-5">{r.t}</h3>
                  <p className="text-sage text-sm leading-relaxed mt-2.5">{r.c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-16 bg-moss" data-testid="day-trips-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal><Eyebrow className="mb-5">Point the car somewhere</Eyebrow></Reveal>
            <Reveal delay={0.08}><h2 className="display-2 text-cream mb-8">Easy days out, <span className="serif-italic text-lavglow">no plans needed.</span></h2></Reveal>
            <Reveal y={40} delay={0.12}>
              <div className="img-frame aspect-[3/4]">
                <img src={IMAGES.oteake} alt="Oteake Conservation Park sign on the track above the farm" className="h-full w-full object-cover" data-testid="oteake-photo" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {TRIPS.map(([n, t, c, url], i) => (
              <Reveal key={t} delay={i * 0.06} y={20}>
                <div className="group flex items-baseline gap-6 md:gap-10 border-t border-white/10 py-7 last:border-b" data-testid={`trip-row-${n}`}>
                  <span className="font-mono text-[0.65rem] text-lavender">{n}</span>
                  <h3 className="font-display text-2xl md:text-3xl text-cream min-w-[160px] md:min-w-[240px]">
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/link inline-flex items-center gap-2 hover:text-lavglow transition-colors"
                        data-testid={`trip-link-${n}`}
                      >
                        {t}
                        <ArrowUpRight size={18} className="text-fern group-hover/link:text-lavglow transition-colors shrink-0" />
                      </a>
                    ) : (
                      t
                    )}
                  </h3>
                  <p className="text-sage text-sm leading-relaxed max-w-md">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BookingCta />
    </>
  );
}
